import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  MessageSquare,
  Repeat,
  ArrowLeft,
  Save,
  Send,
  Timer,
  Trash,
  Settings,
  Target
} from 'lucide-react';

// Helper functions for timezone conversion
// Convert local datetime-local value to UTC ISO string
// IMPORTANT: datetime-local input values are ALWAYS in the user's local timezone
// This function correctly converts them to UTC for database storage
const localTimeToUTC = (localDateTimeString) => {
  if (!localDateTimeString) return '';

  // datetime-local format: "2026-01-12T19:55" (no timezone info, always local)
  // When we create a Date object from this string, JavaScript interprets it as LOCAL time
  // We need to ensure it's treated as local time and then converted to UTC

  // Parse the datetime-local string (format: YYYY-MM-DDTHH:mm)
  const [datePart, timePart] = localDateTimeString.split('T');
  if (!datePart || !timePart) {
    console.error('Invalid datetime-local format:', localDateTimeString);
    return '';
  }

  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);

  // Create a Date object in LOCAL timezone (this is what datetime-local represents)
  // Using new Date(year, month-1, day, hours, minutes) creates a date in LOCAL timezone
  const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);

  // Check if date is valid
  if (isNaN(localDate.getTime())) {
    console.error('Invalid date created from:', localDateTimeString);
    return '';
  }

  // Convert to UTC ISO string (this automatically handles timezone conversion)
  const utcISOString = localDate.toISOString();

  console.log('🕐 Time conversion:', {
    input: localDateTimeString,
    localDate: localDate.toString(),
    utcISO: utcISOString,
    localHours: hours,
    localMinutes: minutes,
    utcHours: localDate.getUTCHours(),
    utcMinutes: localDate.getUTCMinutes()
  });

  return utcISOString;
};

// Convert UTC ISO string to local datetime-local format
const utcToLocalTime = (utcISOString) => {
  if (!utcISOString) return '';

  try {
    // UTC ISO format: "2026-01-12T14:25:00.000Z" or "2026-01-12T14:25:00Z"
    // Create Date object from UTC - JavaScript automatically converts to local timezone
    const utcDate = new Date(utcISOString);

    // Check if date is valid
    if (isNaN(utcDate.getTime())) {
      console.error('Invalid date string:', utcISOString);
      return '';
    }

    // Get local time components (getHours/getMinutes already return local time)
    const year = utcDate.getFullYear();
    const month = String(utcDate.getMonth() + 1).padStart(2, '0');
    const day = String(utcDate.getDate()).padStart(2, '0');
    const hours = String(utcDate.getHours()).padStart(2, '0');
    const minutes = String(utcDate.getMinutes()).padStart(2, '0');

    // Return in datetime-local format: "2026-01-12T19:55"
    const localTimeString = `${year}-${month}-${day}T${hours}:${minutes}`;
    return localTimeString;
  } catch (error) {
    console.error('Error converting UTC to local time:', error, utcISOString);
    return '';
  }
};

// Recurring Schedule Selector Component
const RecurringScheduleSelector = ({ value, onChange }) => {
  const [frequency, setFrequency] = useState('daily'); // daily, weekly, monthly, custom
  const [time, setTime] = useState('09:00');
  const [selectedDays, setSelectedDays] = useState([]);
  const [customCron, setCustomCron] = useState('');
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const isInitialized = useRef(false);
  const lastValueRef = useRef('');
  const isParsingRef = useRef(false);

  const daysOfWeek = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 0, label: 'Sunday' }
  ];

  // Generate cron expression based on settings
  // Converts local time to UTC for consistent scheduling across timezones
  // IMPORTANT: Cron expressions are always in UTC timezone
  const generateCronExpression = useCallback(() => {
    if (frequency === 'custom') {
      // For custom cron, return as-is (user is responsible for UTC)
      return customCron;
    }

    const [hours, minutes] = time.split(':').map(Number);

    // Create a date object with TODAY's date in local timezone
    const localDate = new Date();
    // Set the time in LOCAL timezone (this is what the user selected)
    localDate.setHours(hours, minutes, 0, 0);

    // Get the UTC equivalent of the local time
    // This ensures the cron expression runs at the correct UTC time
    const utcHours = localDate.getUTCHours();
    const utcMinutes = localDate.getUTCMinutes();

    let cronExpr = '';

    switch (frequency) {
      case 'daily':
        cronExpr = `${utcMinutes} ${utcHours} * * *`;
        break;

      case 'weekly':
        if (selectedDays.length === 0) return '';
        const dayPattern = selectedDays.length === 7 ? '*' : selectedDays.join(',');
        cronExpr = `${utcMinutes} ${utcHours} * * ${dayPattern}`;
        break;

      case 'monthly':
        cronExpr = `${utcMinutes} ${utcHours} ${dayOfMonth} * *`;
        break;

      default:
        return '';
    }

    // Log for debugging
    console.log('🕐 Cron expression generation:', {
      frequency,
      localTime: `${hours}:${minutes}`,
      utcTime: `${utcHours}:${utcMinutes}`,
      cronExpr,
      localDate: localDate.toString(),
      utcDate: localDate.toISOString()
    });

    return cronExpr;
  }, [frequency, time, selectedDays, customCron, dayOfMonth]);

  // Parse existing cron expression to populate form (only once when value is provided)
  useEffect(() => {
    // Only parse if value changed and we haven't initialized yet
    if (value && value.trim() && value !== lastValueRef.current) {
      // Mark that we're parsing to prevent onChange from firing
      isParsingRef.current = true;
      lastValueRef.current = value;

      // Try to parse existing cron expression
      const parts = value.trim().split(' ');
      if (parts.length === 5) {
        const [min, hour, day, month, weekday] = parts;

        // Convert UTC time from cron expression to local time for display
        const utcDate = new Date();
        utcDate.setUTCHours(parseInt(hour), parseInt(min), 0, 0);

        // Get local time equivalent
        const localHours = utcDate.getHours();
        const localMinutes = utcDate.getMinutes();

        const hourStr = localHours.toString().padStart(2, '0');
        const minStr = localMinutes.toString().padStart(2, '0');
        setTime(`${hourStr}:${minStr}`);

        // Determine frequency
        if (day === '*' && month === '*' && weekday === '*') {
          setFrequency('daily');
          setSelectedDays([]);
          setDayOfMonth(1);
        } else if (day !== '*' && month === '*' && weekday === '*') {
          setFrequency('monthly');
          setDayOfMonth(parseInt(day) || 1);
          setSelectedDays([]);
        } else if (weekday !== '*') {
          setFrequency('weekly');
          if (weekday.includes(',')) {
            setSelectedDays(weekday.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d)));
          } else {
            setSelectedDays([parseInt(weekday)].filter(d => !isNaN(d)));
          }
          setDayOfMonth(1);
        } else {
          // If we can't determine, set to custom
          setFrequency('custom');
          setCustomCron(value);
        }
      } else if (value.trim()) {
        // If it doesn't match standard format, treat as custom
        setFrequency('custom');
        setCustomCron(value);
      }

      // Mark as initialized and done parsing after state updates
      isInitialized.current = true;
      // Use requestAnimationFrame to ensure state has updated
      requestAnimationFrame(() => {
        isParsingRef.current = false;
      });
    } else if (!value && isInitialized.current) {
      // Reset if value is cleared
      setFrequency('daily');
      setTime('09:00');
      setSelectedDays([]);
      setCustomCron('');
      setDayOfMonth(1);
      lastValueRef.current = '';
      isInitialized.current = false;
      isParsingRef.current = false;
    } else if (!value && !isInitialized.current) {
      // If no value and not initialized, generate default cron expression
      // This happens when schedule_type changes to 'cron' but no cron_expr exists yet
      const defaultCron = generateCronExpression();
      if (defaultCron) {
        lastValueRef.current = defaultCron;
        onChange(defaultCron);
        isInitialized.current = true;
      }
    }
  }, [value, onChange, generateCronExpression]);

  // Update parent when settings change (but not during initialization or parsing)
  useEffect(() => {
    if (isInitialized.current && !isParsingRef.current) {
      const cronExpr = generateCronExpression();
      // Only call onChange if the generated expression is different from the last value
      // This prevents infinite loops
      if (cronExpr && cronExpr !== lastValueRef.current) {
        lastValueRef.current = cronExpr;
        onChange(cronExpr);
      }
    } else if (!isInitialized.current && !isParsingRef.current && !value) {
      // If not initialized, not parsing, and no value provided, generate default cron expression
      // This ensures cron_expr is set when component first mounts with schedule_type='cron' but no existing cron_expr
      const cronExpr = generateCronExpression();
      if (cronExpr) {
        lastValueRef.current = cronExpr;
        onChange(cronExpr);
        isInitialized.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frequency, time, selectedDays, customCron, dayOfMonth]);

  const toggleDay = (day) => {
    setSelectedDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">Recurring Schedule</label>

      {/* Frequency Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 'daily', label: 'Daily', icon: '📅' },
            { value: 'weekly', label: 'Weekly', icon: '📆' },
            { value: 'monthly', label: 'Monthly', icon: '🗓️' },
            { value: 'custom', label: 'Custom', icon: '⚙️' }
          ].map(option => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFrequency(option.value)}
              className={`p-3 text-sm rounded-lg border transition-colors ${frequency === option.value
                ? 'bg-blue-50 border-blue-500 text-blue-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
            >
              <span className="block text-lg mb-1">{option.icon}</span>
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Time Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
        <input
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Weekly Days Selection */}
      {frequency === 'weekly' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Days of Week</label>
          <div className="flex flex-wrap gap-2">
            {daysOfWeek.map(day => (
              <button
                key={day.value}
                type="button"
                onClick={() => toggleDay(day.value)}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors ${selectedDays.includes(day.value)
                  ? 'bg-blue-50 border-blue-500 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
              >
                {day.label}
              </button>
            ))}
          </div>
          {selectedDays.length === 0 && (
            <p className="text-xs text-red-500 mt-1">Please select at least one day</p>
          )}
        </div>
      )}

      {/* Monthly Day Selection */}
      {frequency === 'monthly' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Day of Month</label>
          <select
            value={dayOfMonth}
            onChange={(e) => setDayOfMonth(parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
              <option key={day} value={day}>{day}</option>
            ))}
          </select>
        </div>
      )}

      {/* Custom Cron Expression */}
      {frequency === 'custom' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Custom Cron Expression</label>
          <input
            type="text"
            value={customCron}
            onChange={(e) => setCustomCron(e.target.value)}
            placeholder="30 13 * * * (Every day at 1:30 PM)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">Format: minute hour day month weekday</p>
        </div>
      )}

      {/* Preview */}
      <div className="p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Repeat className="h-4 w-4" />
          <span className="font-medium">Schedule Preview:</span>
        </div>
        <p className="text-sm text-gray-800 mt-1">
          {generateCronExpression() ? (
            <>
              <span className="font-mono bg-white px-2 py-1 rounded border text-xs">
                {generateCronExpression()}
              </span>
              <span className="ml-2">
                {frequency === 'daily' && `Every day at ${time}`}
                {frequency === 'weekly' && `Every ${selectedDays.map(d => daysOfWeek.find(day => day.value === d)?.label).join(', ')} at ${time}`}
                {frequency === 'monthly' && `Every ${dayOfMonth}${dayOfMonth === 1 ? 'st' : dayOfMonth === 2 ? 'nd' : dayOfMonth === 3 ? 'rd' : 'th'} of month at ${time}`}
                {frequency === 'custom' && `Custom schedule: ${customCron}`}
              </span>
            </>
          ) : (
            'Please configure your schedule'
          )}
        </p>
      </div>
    </div>
  );
};

const CreateFlow = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();
  const isEditMode = Boolean(id);
  const [templates, setTemplates] = useState([]);
  const [facebookTemplates, setFacebookTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTemplateType] = useState('facebook'); // Only Facebook templates
  const [loading, setLoading] = useState(false);
  const [newFlow, setNewFlow] = useState({
    name: '',
    trigger: 'scheduled', // Only scheduled trigger type
    status: 'draft', // draft, active, paused, completed
    steps: [],
    schedule_config: {
      schedule_type: 'once', // once, cron
      schedule_time: '',
      cron_expr: ''
    },
    audience: {
      type: 'groups', // 'groups' or 'tags' - mutually exclusive
      include_groups: [],
      include_tags: []
    }
  });

  // Fetch existing flow data if in edit mode
  const fetchExistingFlow = async () => {
    if (!isEditMode || !id) return;

    try {
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      const response = await apiClient.get(`/api/flows/${id}`, { headers });
      const flow = response.data;

      // Parse schedule_config if it's a string
      let scheduleConfig = flow.schedule_config;
      if (typeof scheduleConfig === 'string') {
        try {
          scheduleConfig = JSON.parse(scheduleConfig);
        } catch (e) {
          console.error('Error parsing schedule_config:', e);
          scheduleConfig = {
            schedule_type: 'once',
            schedule_time: '',
            cron_expr: ''
          };
        }
      }

      // Default values if schedule_config is missing
      if (!scheduleConfig) {
        scheduleConfig = {
          schedule_type: 'once',
          schedule_time: '',
          cron_expr: ''
        };
      }

      // Ensure schedule_type is set (default to 'once' if missing)
      if (!scheduleConfig.schedule_type) {
        scheduleConfig.schedule_type = scheduleConfig.cron_expr ? 'cron' : 'once';
      }

      // If schedule_time exists and is in UTC format, convert to local
      if (scheduleConfig.schedule_time && scheduleConfig.schedule_type === 'once') {
        const localTime = utcToLocalTime(scheduleConfig.schedule_time);
        if (localTime) {
          scheduleConfig.schedule_time = localTime;
        }
      } else if (scheduleConfig.schedule_type === 'once' && !scheduleConfig.schedule_time) {
        // If schedule_type is 'once' but no schedule_time, set empty string
        scheduleConfig.schedule_time = '';
      }

      // Parse audience if it's in schedule_config
      let audience = flow.schedule_config?.audience || scheduleConfig.audience;
      if (typeof audience === 'string') {
        try {
          audience = JSON.parse(audience);
        } catch (e) {
          audience = {
            type: 'groups',
            include_groups: [],
            include_tags: []
          };
        }
      }
      if (!audience) {
        audience = {
          type: 'groups',
          include_groups: [],
          include_tags: []
        };
      }

      // Ensure schedule_time is properly formatted for datetime-local input
      let finalScheduleTime = scheduleConfig.schedule_time || '';
      if (finalScheduleTime && scheduleConfig.schedule_type === 'once') {
        // Make sure it's in the correct format (YYYY-MM-DDTHH:mm)
        // If it's already in the right format, use it; otherwise it should have been converted
        if (!finalScheduleTime.includes('T')) {
          // If somehow we don't have T, try to format it
          const dateObj = new Date(finalScheduleTime);
          if (!isNaN(dateObj.getTime())) {
            finalScheduleTime = utcToLocalTime(finalScheduleTime);
          }
        }
      }

      const finalScheduleConfig = {
        schedule_type: scheduleConfig.schedule_type || 'once',
        schedule_time: finalScheduleTime,
        cron_expr: scheduleConfig.cron_expr || ''
      };

      // Update state in a single operation to prevent multiple re-renders and lag
      setNewFlow({
        name: flow.name || '',
        trigger: flow.trigger_type || 'scheduled',
        status: flow.status || 'draft',
        steps: flow.steps || [],
        schedule_config: finalScheduleConfig,
        audience: audience
      });
    } catch (error) {
      console.error('Error fetching campaign:', error);
      toast.error('Failed to load campaign data');
      navigate('/flows');
    }
  };

  // Fetch templates, groups, and tags
  useEffect(() => {
    if (currentWorkspace) {
      fetchTemplates();
      fetchGroups();
      fetchTags();
      if (isEditMode) {
        fetchExistingFlow();
      }
    }
  }, [currentWorkspace, isEditMode, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update current templates when template type changes
  useEffect(() => {
    updateCurrentTemplates();
  }, [selectedTemplateType, facebookTemplates]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTemplates = async () => {
    try {
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};

      // Only fetch Facebook templates

      // Fetch Facebook templates
      let facebookData = [];
      try {
        console.log('Fetching Facebook templates for workspace:', currentWorkspace?.id);
        const facebookResponse = await apiClient.get('/api/whatsapp/templates?days=365', { headers });
        if (facebookResponse.data && facebookResponse.data.templates && Array.isArray(facebookResponse.data.templates)) {
          // Format Facebook templates to match expected format
          // Use a simple approach: facebook_templateName for uniqueness
          facebookData = facebookResponse.data.templates.map((template, index) => ({
            ...template,
            id: `facebook_${template.name}`, // Simple facebook_ prefix
            type: 'facebook',
            originalIndex: index // Store original index for uniqueness
          }));
        }
        setFacebookTemplates(facebookData);
        console.log('Facebook templates fetched successfully:', facebookData.length);
        console.log('Template IDs:', facebookData.map(t => t.id));

        // Check for duplicates
        const templateNames = facebookData.map(t => t.name);
        const uniqueNames = [...new Set(templateNames)];
        if (templateNames.length !== uniqueNames.length) {
          console.warn('Duplicate template names found:', templateNames.filter((name, index) => templateNames.indexOf(name) !== index));
        }
      } catch (facebookError) {
        console.error('Failed to fetch Facebook templates:', {
          error: facebookError.message,
          response: facebookError.response?.data,
          status: facebookError.response?.status,
          workspace: currentWorkspace?.id
        });
        setFacebookTemplates([]);
        // Show user-friendly error message
        if (facebookError.response?.status === 400) {
          toast.error('WhatsApp settings not configured. Please configure WhatsApp settings in the Settings page.');
        } else {
          toast.error('Failed to fetch Facebook templates. Please check your WhatsApp configuration.');
        }
      }

      // Set current templates based on selected type
      updateCurrentTemplates();

      console.log('Fetched Facebook templates:', facebookData.length);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to fetch templates');
      setFacebookTemplates([]);
    }
  };

  const updateCurrentTemplates = () => {
    setTemplates(facebookTemplates);
  };

  // Extract parameters from Facebook template
  const extractTemplateParameters = (template) => {
    if (!template || !template.components) return [];

    const bodyComponent = template.components.find(c => c.type === 'BODY');
    if (!bodyComponent || !bodyComponent.text) return [];

    const text = bodyComponent.text;
    const parameters = [];
    let match;
    const regex = /\{\{(\d+)\}\}/g;

    while ((match = regex.exec(text)) !== null) {
      const paramIndex = parseInt(match[1]) - 1; // Convert to 0-based index
      if (!parameters.includes(paramIndex)) {
        parameters.push(paramIndex);
      }
    }

    return parameters.sort((a, b) => a - b);
  };

  const fetchGroups = async () => {
    try {
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      const groupsRes = await apiClient.get('/api/contacts/groups/list', { headers });

      // Handle groups response
      let groupsData = [];
      if (groupsRes.data && Array.isArray(groupsRes.data)) {
        groupsData = groupsRes.data;
      } else if (groupsRes.data && groupsRes.data.groups && Array.isArray(groupsRes.data.groups)) {
        groupsData = groupsRes.data.groups;
      }
      setGroups(groupsData);

      console.log('Fetched groups:', groupsData.length);
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error('Failed to fetch groups');
      // Set empty array on error
      setGroups([]);
    }
  };

  const fetchTags = async () => {
    try {
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      const tagsRes = await apiClient.get('/api/contacts/tags/list', { headers });

      // Handle tags response
      let tagsData = [];
      if (tagsRes.data && Array.isArray(tagsRes.data)) {
        tagsData = tagsRes.data;
      } else if (tagsRes.data && tagsRes.data.tags && Array.isArray(tagsRes.data.tags)) {
        tagsData = tagsRes.data.tags;
      }
      setTags(tagsData);

      console.log('Fetched tags:', tagsData.length);
    } catch (error) {
      console.error('Error fetching tags:', error);
      toast.error('Failed to fetch tags');
      // Set empty array on error
      setTags([]);
    }
  };

  // Add step to flow (only message steps are supported)
  const addStep = (type = 'message') => {
    const newStep = {
      id: Date.now(),
      type: 'message', // Only message steps are supported
      template_id: '',
      template_type: selectedTemplateType,
      parameters: {},
      delay: 0,
      delay_unit: 'minutes'
    };
    setNewFlow({ ...newFlow, steps: [...newFlow.steps, newStep] });
  };

  // Remove step from flow
  const removeStep = (stepId) => {
    setNewFlow({ ...newFlow, steps: newFlow.steps.filter(step => step.id !== stepId) });
  };

  // Update step
  const updateStep = (stepId, updates) => {
    setNewFlow({
      ...newFlow,
      steps: newFlow.steps.map(step =>
        step.id === stepId ? { ...step, ...updates } : step
      )
    });
  };

  // Handle template selection and extract parameters
  const handleTemplateSelection = (stepId, templateId, templateType) => {
    const selectedTemplate = templates.find(t => t.id === templateId);
    if (!selectedTemplate) {
      console.error('Template not found:', templateId, 'Available templates:', templates.map(t => t.id));
      return;
    }

    const updates = {
      template_id: templateId,
      template_type: templateType
    };

    // Extract parameters for Facebook templates
    if (templateType === 'facebook' && selectedTemplate.components) {
      const paramIndices = extractTemplateParameters(selectedTemplate);
      const parameters = {};
      paramIndices.forEach(index => {
        parameters[`param_${index + 1}`] = '';
      });
      updates.parameters = parameters;
    } else {
      updates.parameters = {};
    }

    updateStep(stepId, updates);
  };

  // Create or update flow
  const handleCreateFlow = async () => {
    try {
      if (!newFlow.name.trim()) {
        toast.error('Please enter campaign name');
        return;
      }

      if (newFlow.steps.length === 0) {
        toast.error('Please add at least one step to the flow');
        return;
      }

      if (newFlow.audience.type === 'groups' && newFlow.audience.include_groups.length === 0) {
        toast.error('Please select at least one group for the audience');
        return;
      }

      if (newFlow.audience.type === 'tags' && newFlow.audience.include_tags.length === 0) {
        toast.error('Please select at least one tag for the audience');
        return;
      }

      // Validate message steps have templates
      const messageSteps = newFlow.steps.filter(step => step.type === 'message');
      for (const step of messageSteps) {
        if (!step.template_id) {
          toast.error(`Please select a WhatsApp template for step ${newFlow.steps.indexOf(step) + 1}`);
          return;
        }
      }

      // Validate schedule configuration
      if (newFlow.schedule_config.schedule_type === 'once') {
        if (!newFlow.schedule_config.schedule_time || newFlow.schedule_config.schedule_time.trim() === '') {
          toast.error('Please set a schedule time for "Run Once" campaigns');
          return;
        }
      } else if (newFlow.schedule_config.schedule_type === 'cron') {
        if (!newFlow.schedule_config.cron_expr || newFlow.schedule_config.cron_expr.trim() === '') {
          toast.error('Please configure the recurring schedule');
          return;
        }
      } else {
        toast.error('Please configure the schedule (Run Once or Recurring)');
        return;
      }

      setLoading(true);
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};

      // Prepare flow data with UTC conversion for schedule_time
      const flowData = { ...newFlow };
      if (flowData.schedule_config?.schedule_type === 'once' && flowData.schedule_config?.schedule_time) {
        // Convert local time to UTC before sending
        // schedule_time from datetime-local input is in user's local timezone
        // We need to convert it to UTC ISO string for database storage
        const originalLocalTime = flowData.schedule_config.schedule_time;
        const utcScheduleTime = localTimeToUTC(originalLocalTime);
        flowData.schedule_config = {
          ...flowData.schedule_config,
          schedule_time: utcScheduleTime
        };

        console.log('📅 Schedule time conversion (once):', {
          localTime: originalLocalTime,
          utcTime: utcScheduleTime,
          scheduleType: 'once'
        });
      } else if (flowData.schedule_config?.schedule_type === 'cron' && flowData.schedule_config?.cron_expr) {
        // For cron expressions, log to verify they're in UTC
        console.log('📅 Cron schedule (recurring):', {
          cronExpr: flowData.schedule_config.cron_expr,
          scheduleType: 'cron',
          note: 'Cron expressions are always interpreted in UTC timezone by the scheduler'
        });
      }

      if (isEditMode) {
        await apiClient.put(`/api/flows/${id}`, flowData, { headers });
        toast.success('Campaign updated successfully');
      } else {
        await apiClient.post('/api/flows', flowData, { headers });
        toast.success('Campaign created successfully');
      }

      // Invalidate and refetch flows list to show the new/updated campaign
      await queryClient.invalidateQueries(['flows', currentWorkspace?.id]);

      navigate('/flows');
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} campaign:`, error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} campaign: ` + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/flows')}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {isEditMode ? 'Edit Campaign' : 'Create Campaign'}
                </h1>
                <p className="text-sm text-gray-600">Build automated message sequences</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/flows')}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFlow}
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Save className="h-4 w-4" />
                <span>
                  {loading
                    ? (isEditMode ? 'Updating...' : 'Creating...')
                    : (isEditMode ? 'Update Campaign' : 'Create Campaign')
                  }
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Flow Configuration */}
          <div className="bg-white rounded-xl shadow-sm border p-8">
            <h2 className="text-lg font-medium text-gray-900 flex items-center mb-6">
              <Settings className="h-5 w-5 mr-2" />
              Campaign Configuration
            </h2>

            {/* Grid Layout for Better Organization */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 1. Campaign Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name *</label>
                <input
                  type="text"
                  value={newFlow.name}
                  onChange={(e) => setNewFlow({ ...newFlow, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="e.g., Welcome Series"
                />
              </div>

              {/* 2. Audience Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Audience Type *
                  <span className="text-xs text-gray-500 ml-1 font-normal">(Choose how to target your audience)</span>
                </label>
                <select
                  value={newFlow.audience.type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setNewFlow({
                      ...newFlow,
                      audience: {
                        type: newType,
                        include_groups: newType === 'groups' ? newFlow.audience.include_groups : [],
                        include_tags: newType === 'tags' ? newFlow.audience.include_tags : []
                      }
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="groups">Target by Groups</option>
                  <option value="tags">Target by Tags</option>
                </select>
              </div>

              {/* 3. Target Groups */}
              {newFlow.audience.type === 'groups' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Groups
                    <span className="text-xs text-gray-500 ml-1">(Select one or more groups)</span>
                  </label>
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                    {!Array.isArray(groups) || groups.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">No groups available</p>
                    ) : (
                      groups.map((group) => {
                        const isSelected = newFlow.audience.include_groups.includes(group.name);
                        return (
                          <label
                            key={group.name}
                            className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${isSelected
                              ? 'bg-blue-50 border-blue-300 shadow-sm'
                              : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                              }`}
                          >
                            <div className="relative">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  const include_groups = e.target.checked
                                    ? [...newFlow.audience.include_groups, group.name]
                                    : newFlow.audience.include_groups.filter(g => g !== group.name);
                                  setNewFlow({ ...newFlow, audience: { ...newFlow.audience, include_groups } });
                                }}
                                className="w-5 h-5 rounded border-2 border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2"
                              />
                              {isSelected && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <span className={`text-sm font-medium ${isSelected ? 'text-blue-900' : 'text-gray-700'}`}>
                                {group.name}
                              </span>
                              <span className={`text-xs ml-2 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`}>
                                ({group.count || 0} contacts)
                              </span>
                            </div>
                            {isSelected && (
                              <div className="flex items-center">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Selected
                                </span>
                              </div>
                            )}
                          </label>
                        );
                      })
                    )}
                  </div>
                  {newFlow.audience.include_groups.length > 0 ? (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium text-green-800">
                          {newFlow.audience.include_groups.length} group(s) selected
                        </span>
                      </div>
                      <p className="text-xs text-green-600 mt-1">
                        Campaign will be sent to all contacts in these groups
                      </p>
                    </div>
                  ) : (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium text-yellow-800">
                          No groups selected
                        </span>
                      </div>
                      <p className="text-xs text-yellow-600 mt-1">
                        Please select at least one group to continue
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* 4. Target Tags */}
              {newFlow.audience.type === 'tags' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Tags
                    <span className="text-xs text-gray-500 ml-1">(Select one or more tags)</span>
                  </label>
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                    {!Array.isArray(tags) || tags.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">No tags available</p>
                    ) : (
                      tags.map((tag) => {
                        const isSelected = newFlow.audience.include_tags.includes(tag.name);
                        return (
                          <label
                            key={tag.name}
                            className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${isSelected
                              ? 'bg-purple-50 border-purple-300 shadow-sm'
                              : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                              }`}
                          >
                            <div className="relative">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  const include_tags = e.target.checked
                                    ? [...newFlow.audience.include_tags, tag.name]
                                    : newFlow.audience.include_tags.filter(t => t !== tag.name);
                                  setNewFlow({ ...newFlow, audience: { ...newFlow.audience, include_tags } });
                                }}
                                className="w-5 h-5 rounded border-2 border-gray-300 text-purple-600 focus:ring-purple-500 focus:ring-2"
                              />
                              {isSelected && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <svg className="w-3 h-3 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <span className={`text-sm font-medium ${isSelected ? 'text-purple-900' : 'text-gray-700'}`}>
                                {tag.name}
                              </span>
                              <span className={`text-xs ml-2 ${isSelected ? 'text-purple-600' : 'text-gray-500'}`}>
                                ({tag.count || 0} contacts)
                              </span>
                            </div>
                            {isSelected && (
                              <div className="flex items-center">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  Selected
                                </span>
                              </div>
                            )}
                          </label>
                        );
                      })
                    )}
                  </div>
                  {newFlow.audience.include_tags.length > 0 ? (
                    <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-purple-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium text-purple-800">
                          {newFlow.audience.include_tags.length} tag(s) selected
                        </span>
                      </div>
                      <p className="text-xs text-purple-600 mt-1">
                        Campaign will be sent to all contacts with these tags
                      </p>
                    </div>
                  ) : (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium text-yellow-800">
                          No tags selected
                        </span>
                      </div>
                      <p className="text-xs text-yellow-600 mt-1">
                        Please select at least one tag to continue
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Audience Summary */}
              {((newFlow.audience.type === 'groups' && newFlow.audience.include_groups.length > 0) ||
                (newFlow.audience.type === 'tags' && newFlow.audience.include_tags.length > 0)) && (
                  <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <Target className="h-5 w-5 text-blue-600 mr-2" />
                      <h3 className="text-sm font-medium text-blue-900">Campaign Audience Summary</h3>
                    </div>

                    <div className="space-y-2">
                      {newFlow.audience.type === 'groups' && newFlow.audience.include_groups.length > 0 && (
                        <div className="flex items-center">
                          <span className="text-xs font-medium text-blue-700 mr-2">Groups:</span>
                          <div className="flex flex-wrap gap-1">
                            {newFlow.audience.include_groups.map(groupName => (
                              <span key={groupName} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {groupName}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {newFlow.audience.type === 'tags' && newFlow.audience.include_tags.length > 0 && (
                        <div className="flex items-center">
                          <span className="text-xs font-medium text-purple-700 mr-2">Tags:</span>
                          <div className="flex flex-wrap gap-1">
                            {newFlow.audience.include_tags.map(tagName => (
                              <span key={tagName} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                {tagName}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <p className="text-xs text-blue-600 mt-2">
                      Campaign will be sent to contacts {newFlow.audience.type === 'groups' ? 'in the selected groups' : 'with the selected tags'}
                    </p>
                  </div>
                )}

              {/* Schedule Section */}
              <div className="md:col-span-2 border-t border-gray-200 pt-6 mt-2">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Schedule Configuration</h3>

                {/* Schedule Type and Time in one row on desktop */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Schedule Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Type</label>
                    <select
                      value={newFlow.schedule_config.schedule_type}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setNewFlow({
                          ...newFlow,
                          schedule_config: {
                            ...newFlow.schedule_config,
                            schedule_type: newType,
                            // Clear the other field when switching types
                            schedule_time: newType === 'once' ? newFlow.schedule_config.schedule_time : '',
                            cron_expr: newType === 'cron' ? newFlow.schedule_config.cron_expr : ''
                          }
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="once">Run Once</option>
                      <option value="cron">Recurring</option>
                    </select>
                  </div>

                  {/* Schedule Time (Once) - Always visible, shows input or recurring selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {newFlow.schedule_config.schedule_type === 'once' ? 'Schedule Time *' : 'Recurring Schedule'}
                    </label>
                    {newFlow.schedule_config.schedule_type === 'once' ? (
                      <input
                        type="datetime-local"
                        value={newFlow.schedule_config.schedule_time}
                        onChange={(e) => setNewFlow({
                          ...newFlow,
                          schedule_config: { ...newFlow.schedule_config, schedule_time: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    ) : (
                      <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm text-gray-600">
                        Configure below
                      </div>
                    )}
                  </div>
                </div>

                {/* Recurring Schedule Configuration - Full width below */}
                {newFlow.schedule_config.schedule_type === 'cron' && (
                  <div className="mt-4">
                    <RecurringScheduleSelector
                      value={newFlow.schedule_config.cron_expr || ''}
                      onChange={(cronExpr) => {
                        console.log('RecurringScheduleSelector onChange:', cronExpr);
                        setNewFlow({
                          ...newFlow,
                          schedule_config: { ...newFlow.schedule_config, cron_expr: cronExpr || '' }
                        });
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Flow Steps */}
          <div className="bg-white rounded-xl shadow-sm border p-8 lg:sticky lg:top-6 lg:self-start">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <MessageSquare className="h-5 w-5 mr-2" />
                Campaign Steps
              </h2>
              <div className="flex space-x-2">
                <button
                  onClick={() => addStep('message')}
                  className="flex items-center space-x-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  <Send className="h-4 w-4" />
                  <span>Add Message Step</span>
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {newFlow.steps.map((step, index) => (
                <div key={step.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full font-semibold">
                        Step {index + 1}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Send className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-semibold text-gray-800">
                          Message Step
                        </span>
                      </div>
                      {index > 0 && step.delay > 0 && (
                        <div className="flex items-center space-x-1 text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                          <Timer className="h-3 w-3" />
                          <span>Wait {step.delay} {step.delay_unit}</span>
                        </div>
                      )}
                      {index === 0 && (
                        <div className="flex items-center space-x-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                          <Send className="h-3 w-3" />
                          <span>Sends immediately</span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => removeStep(step.id)}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove this step"
                    >
                      <Trash className="h-4 w-4" />
                    </button>
                  </div>

                  {step.type === 'message' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Template Type</label>
                        <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 mb-3">
                          📘 Facebook Templates (Only)
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Template *</label>
                        <select
                          value={step.template_id || ''}
                          onChange={(e) => {
                            const selectedTemplateId = e.target.value;
                            console.log('Template selection:', {
                              selectedTemplateId,
                              availableTemplates: templates.map(t => ({ id: t.id, name: t.name }))
                            });
                            handleTemplateSelection(step.id, selectedTemplateId, 'facebook');
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select a Facebook template</option>
                          {Array.isArray(templates) && templates.map((template, index) => (
                            <option key={`${template.id}-${template.originalIndex || index}`} value={template.id}>
                              {template.name}
                              {template.status && ` [${template.status}]`}
                              {template.language && ` (${template.language})`}
                            </option>
                          ))}
                        </select>
                        {(!Array.isArray(templates) || templates.length === 0) && (
                          <div className="text-xs text-gray-500 mt-1">
                            {selectedTemplateType === 'facebook' ? (
                              <div>
                                <p>No Facebook templates available.</p>
                                <p className="text-red-500">Please configure WhatsApp settings in the Settings page to fetch Facebook templates.</p>
                              </div>
                            ) : (
                              <p>No Facebook templates available. Check your WhatsApp Business account.</p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Template Preview - Always show for selected templates */}
                      {step.template_id && (() => {
                        const selectedTemplate = templates.find(t => t.id === step.template_id);
                        if (!selectedTemplate || !selectedTemplate.components) return null;

                        const bodyComponent = selectedTemplate.components.find(c => c.type === 'BODY');
                        if (!bodyComponent || !bodyComponent.text) return null;

                        // Create preview with sample contact data
                        const sampleContact = {
                          name: 'John Doe',
                          phone: '+1234567890',
                          email: 'john@example.com',
                          group_name: 'VIP Customers',
                          tags: ['Premium', 'Active']
                        };

                        let previewText = bodyComponent.text;

                        // If template has parameters, replace them with sample data
                        if (step.parameters && Object.keys(step.parameters).length > 0) {
                          Object.keys(step.parameters).forEach((paramKey, index) => {
                            const placeholder = `{{${index + 1}}}`;
                            let value = step.parameters[paramKey] || `[Parameter ${index + 1}]`;

                            // Replace contact field placeholders with sample data for preview
                            value = value.replace(/\{\{contact\.name\}\}/g, sampleContact.name);
                            value = value.replace(/\{\{contact\.phone\}\}/g, sampleContact.phone);
                            value = value.replace(/\{\{contact\.email\}\}/g, sampleContact.email);
                            value = value.replace(/\{\{contact\.group_name\}\}/g, sampleContact.group_name);
                            value = value.replace(/\{\{contact\.tags\}\}/g, sampleContact.tags.join(', '));

                            previewText = previewText.replace(new RegExp(placeholder, 'g'), value);
                          });
                        }

                        return (
                          <div className="mt-3 space-y-2">
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <p className="text-xs font-medium text-blue-800 mb-1">Template Preview:</p>
                              <p className="text-sm text-blue-700">{previewText}</p>
                            </div>
                            {step.parameters && Object.keys(step.parameters).length > 0 && (
                              <div className="p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
                                <strong>Sample data used:</strong> John Doe, +1234567890, john@example.com, VIP Customers, Premium, Active
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Template Parameters for Facebook Templates */}
                      {step.template_type === 'facebook' && step.parameters && Object.keys(step.parameters).length > 0 && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700">Template Parameters</label>
                            <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                              💡 Use contact fields: {`{{contact.name}}`}, {`{{contact.phone}}`}, {`{{contact.email}}`}, {`{{contact.group_name}}`}, {`{{contact.tags}}`}
                            </div>
                          </div>

                          {Object.keys(step.parameters).map((paramKey, index) => (
                            <div key={paramKey} className="border border-gray-200 rounded-lg p-4 bg-white">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Parameter {index + 1} ({`{{${index + 1}}}`})
                              </label>
                              <div className="space-y-3">
                                <input
                                  type="text"
                                  value={step.parameters[paramKey] || ''}
                                  onChange={(e) => {
                                    const newParameters = { ...step.parameters };
                                    newParameters[paramKey] = e.target.value;
                                    updateStep(step.id, { parameters: newParameters });
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                  placeholder={`Enter value for parameter ${index + 1} (e.g., {{contact.name}})`}
                                />

                                {/* Quick Insert Buttons */}
                                <div className="flex flex-wrap gap-1">
                                  <span className="text-xs text-gray-500 mr-2">Quick insert:</span>
                                  {[
                                    { field: 'name', label: 'Name', color: 'blue' },
                                    { field: 'phone', label: 'Phone', color: 'green' },
                                    { field: 'email', label: 'Email', color: 'purple' },
                                    { field: 'group_name', label: 'Group', color: 'orange' },
                                    { field: 'tags', label: 'Tags', color: 'pink' }
                                  ].map(field => (
                                    <button
                                      key={field.field}
                                      type="button"
                                      onClick={() => {
                                        const newParameters = { ...step.parameters };
                                        const currentValue = newParameters[paramKey] || '';
                                        const newValue = currentValue + `{{contact.${field.field}}}`;
                                        newParameters[paramKey] = newValue;
                                        updateStep(step.id, { parameters: newParameters });
                                      }}
                                      className={`px-2 py-1 text-xs rounded hover:opacity-80 ${field.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                                        field.color === 'green' ? 'bg-green-100 text-green-700' :
                                          field.color === 'purple' ? 'bg-purple-100 text-purple-700' :
                                            field.color === 'orange' ? 'bg-orange-100 text-orange-700' :
                                              'bg-pink-100 text-pink-700'
                                        }`}
                                      title={`Insert {{contact.${field.field}}}`}
                                    >
                                      {field.label}
                                    </button>
                                  ))}
                                </div>

                                {/* Preview */}
                                {step.parameters[paramKey] && (
                                  <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                                    <strong>Preview:</strong> {step.parameters[paramKey].replace(/\{\{contact\.(\w+)\}\}/g, (match, field) => {
                                      const sampleData = {
                                        name: 'John Doe',
                                        phone: '+1234567890',
                                        email: 'john@example.com',
                                        group_name: 'VIP Customers',
                                        tags: 'Premium, Active'
                                      };
                                      return sampleData[field] || match;
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          <p className="text-xs text-gray-500">
                            These parameters will be used to fill in the template variables when the campaign runs. Contact field placeholders will be replaced with actual contact data.
                          </p>
                        </div>
                      )}

                      {/* Wait Time Before Sending This Message - Only show for steps after the first one */}
                      {index > 0 && (
                        <div className="border-t border-gray-200 pt-4 mt-4">
                          <div className="flex items-center space-x-2 mb-3">
                            <Timer className="h-4 w-4 text-gray-500" />
                            <label className="block text-sm font-medium text-gray-700">
                              Wait Time Before Sending
                            </label>
                          </div>
                          <p className="text-xs text-gray-500 mb-3">
                            How long to wait after the previous step before sending this message.
                          </p>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Duration</label>
                              <input
                                type="number"
                                value={step.delay || 0}
                                onChange={(e) => updateStep(step.id, { delay: parseInt(e.target.value) || 0 })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                min="0"
                                placeholder="0"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Time Unit</label>
                              <select
                                value={step.delay_unit || 'minutes'}
                                onChange={(e) => updateStep(step.id, { delay_unit: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              >
                                <option value="minutes">Minutes</option>
                                <option value="hours">Hours</option>
                                <option value="days">Days</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                </div>
              ))}

              {newFlow.steps.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <h3 className="text-md font-medium text-gray-900 mb-2">No steps added yet</h3>
                  <p className="text-sm text-gray-600 mb-3">Add message steps to build your campaign. Each step can have its own wait time.</p>
                  <button
                    onClick={() => addStep('message')}
                    className="flex items-center space-x-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm mx-auto"
                  >
                    <Send className="h-4 w-4" />
                    <span>Add First Template</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateFlow;

